import { SuppliesTrackableProductType } from "@redotech/crdb-enums/product/supplies-trackable-product-type";
import {
  hasSafetyStockAttribute,
  ON_HAND_INVENTORY_ATTRIBUTE,
  ORDER_INTERVAL_ATTRIBUTE,
  ORDER_INTERVAL_FREQUENCY_ATTRIBUTE,
  REORDER_QUANTITY_ATTRIBUTE,
  SAFETY_STOCK_ATTRIBUTE,
  SUPPLIES_PROMOTION_ATTRIBUTE,
  TIED_PARCEL_OIDS_ATTRIBUTE,
  TRACKABLE_TYPE_ATTRIBUTE,
} from "@redotech/redo-supplies-common/cart-attributes";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo } from "react";

function tiedParcelCount(value: string): number {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

function formatTrackableType(value: string): string {
  switch (value) {
    case SuppliesTrackableProductType.LabelPaper:
      return "Label paper";
    case SuppliesTrackableProductType.ParcelPackaging:
      return "Parcel packaging";
    default:
      return value;
  }
}

export function formatCartAttributeLabel(attribute: {
  key: string;
  value: string;
}): string {
  switch (attribute.key) {
    case ON_HAND_INVENTORY_ATTRIBUTE:
      return `On-hand inventory: ${attribute.value}`;
    case SAFETY_STOCK_ATTRIBUTE:
      return `Safety stock: ${attribute.value}`;
    case REORDER_QUANTITY_ATTRIBUTE:
      return `Reorder quantity: ${attribute.value}`;
    case TRACKABLE_TYPE_ATTRIBUTE:
      return `Type: ${formatTrackableType(attribute.value)}`;
    case TIED_PARCEL_OIDS_ATTRIBUTE:
      return `Tied parcels: ${tiedParcelCount(attribute.value)}`;
    default:
      return `${attribute.key}: ${attribute.value}`;
  }
}

const SCHEDULE_KEYS = [
  ORDER_INTERVAL_ATTRIBUTE,
  ORDER_INTERVAL_FREQUENCY_ATTRIBUTE,
];

// Rendered specially (schedule) or internal-only; excluded from the plain list.
const NON_LABEL_KEYS = [...SCHEDULE_KEYS, SUPPLIES_PROMOTION_ATTRIBUTE];

/** Renders the interval pair as one line, e.g. "Repeats: every 2 months". */
function formatSchedule(interval: string, frequency: string): string {
  const count = Number(frequency);
  if (!Number.isFinite(count) || count < 1) {
    return `Repeats: every ${interval}`;
  }
  return count === 1
    ? `Repeats: every ${interval}`
    : `Repeats: every ${count} ${interval}s`;
}

/** Whether a line carries a recurring (subscription/inventory) setup badge. */
export function hasRecurringBadge(
  attributes: { key: string; value: string }[],
): boolean {
  return (
    hasSafetyStockAttribute(attributes) ||
    attributes.some((a) => a.key === ORDER_INTERVAL_ATTRIBUTE)
  );
}

/**
 * Badge marking a cart line as recurring, with its setup details on hover.
 * Renders nothing for ordinary one-time items.
 */
export const CartItemRecurringBadge = memo(function CartItemRecurringBadge({
  attributes,
}: {
  attributes: { key: string; value: string }[];
}) {
  const isInventoryTriggered = hasSafetyStockAttribute(attributes);
  if (!hasRecurringBadge(attributes)) {
    return null;
  }

  return (
    <Tooltip title={<CartAttributesTooltipContent attributes={attributes} />}>
      <span>
        <RedoBadge
          color={isInventoryTriggered ? "orange" : "blue"}
          size="xs"
          text={isInventoryTriggered ? "Inventory-triggered" : "Subscription"}
        />
      </span>
    </Tooltip>
  );
});

export const CartAttributesTooltipContent = memo(
  function CartAttributesTooltipContent({
    attributes,
  }: {
    attributes: { key: string; value: string }[];
  }) {
    const interval = attributes.find(
      (a) => a.key === ORDER_INTERVAL_ATTRIBUTE,
    )?.value;
    const frequency = attributes.find(
      (a) => a.key === ORDER_INTERVAL_FREQUENCY_ATTRIBUTE,
    )?.value;
    const rest = attributes.filter((a) => !NON_LABEL_KEYS.includes(a.key));

    return (
      <Flex dir="column" gap="xs">
        {interval != null && (
          <Text fontSize="xs">
            {formatSchedule(interval, frequency ?? "1")}
          </Text>
        )}
        {rest.map((attribute) => (
          <Text fontSize="xs" key={attribute.key}>
            {formatCartAttributeLabel(attribute)}
          </Text>
        ))}
      </Flex>
    );
  },
);
